import React, { useState, useEffect } from "react";

import {
    Box,
    Divider,
    Grid,
    Typography,
    Button,
    styled,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    ListItemText,
    Checkbox,
    FormControl,
    FormGroup,
    FormControlLabel,
    ButtonGroup
} from '@mui/material';

import { yellow, grey } from "@mui/material/colors";
import { useNavigate, useParams } from "react-router-dom";

import { useDocuments, documentTypes, targetTypes } from "../../../hooks/api/useDocuments";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useSystemUtils } from "../../../hooks/useSystemUtils";
import { useAppContextFeatures } from "../../../context/AppContext";
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonRestricted from '../../common/CommonRestricted';

import { CommonPdf, renderModes, documentTypes as commonPdfDocumentTypes } from "../../common/CommonPdf";

import DateTimePickerComponent from "../../common/DateTimePickerComponent";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 800
}))

const StyledContent = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem'
}))

const StyledContent2 = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    mt: 2
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
    mt: 1,
    mb: 0,
    borderStyle: 'dashed'
}))

const styles = {
    title: {
        fontSize: 14,
        fontWeight: '800'
    },
    subTitle: {
        fontSize: 12
    },
    borderLeft: {
        borderLeftWidth: 1,
        borderLeftStyle: 'dashed',
        borderLeftColor: grey[500]
    }
}

export default function ExecuteStockTake({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const { id } = useParams();

    const { setSystemLoading, openCustomDrawer } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { get } = useDocuments({ documentType: documentTypes.stockTake, targetType: targetTypes.stockTake });
    const { formatCurrencyDisplay, currencyAbb } = useSystemUtils();
    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.stockTake.id)

    const navigate = useNavigate();
    const [data, setData] = useState()

    const fetchData = async () => {

        setSystemLoading(true);

        try {

            let response = await get({ id });
            // console.log('response?.data?.items', response?.data)
            setData(response?.data)

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

    }

    useEffect(() => {

        fetchData();

    }, [])

    const RenderTable = () => {

        if (!data) return <></>

        return (
            <TableContainer
                // component={Paper}
                sx={{
                    // minHeight: 400,
                    // maxHeight: 400
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                >
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" colSpan={3} sx={{ backgroundColor: 'primary.main', color: grey[100] }}>
                                {t('stockTake')}
                            </TableCell>
                            <TableCell align="center" colSpan={3} sx={{ backgroundColor: 'primary.main', color: grey[100] }}>
                                {t('currentPreview')}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell align="center" colSpan={3} sx={{ borderBottomWidth: 1, borderBottomColor: grey[500] }}>
                                <Typography>
                                    {data?.document.doc_number} ({data?.document.doc_date})
                                </Typography>
                                <Typography>
                                    {data?.document?.remark ? data.document.remark : "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center" colSpan={3} sx={{ borderBottomWidth: 1, borderBottomColor: grey[500] }}>
                                <Grid container>
                                    <Grid item md={7}>
                                        <DateTimePickerComponent
                                            slotProps={{
                                                textField: {
                                                    // error: Boolean(touched[name]) && Boolean(errors[name]),
                                                    // helperText: touched[name] && errors[name],
                                                    // size: "small",
                                                    // margin: "dense",
                                                    borderColor: grey[100]
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={5} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                        <FormGroup>
                                            {/* <FormControlLabel control={<Checkbox defaultChecked />} label="Label" /> */}
                                            {/* <FormControlLabel required control={<Checkbox />} label="Required" /> */}
                                            <FormControlLabel control={<Checkbox />} label={t("execute")} />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell width={50}>#</TableCell>
                            <TableCell width={250}>{t('name')}</TableCell>
                            <TableCell width={120}>{t('qty')}</TableCell>
                            <TableCell width={120} sx={[styles.borderLeft, {}]}>Qty.</TableCell>
                            <TableCell align="right" width={120} sx={{}}>{t('suggestion')}</TableCell>
                            <TableCell align="right" sx={{}}>
                                {t('actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            data?.items.map((row, index) => {

                                return (
                                    <TableRow key={row.id}>
                                        <TableCell>{(index + 1)}</TableCell>
                                        <TableCell>
                                            <ListItemText
                                                primary={<Typography sx={styles.title}>{row?.code}</Typography>}
                                                secondary={<Typography sx={styles.subTitle}>{row?.item_name}</Typography>}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <ListItemText
                                                primary={<Typography sx={styles.title}>{parseFloat(row?.qty)}</Typography>}
                                                secondary={<Typography sx={styles.subTitle}>{row?.uom_name}</Typography>}
                                            />
                                        </TableCell>
                                        <TableCell align="left" sx={[styles.borderLeft, {}]}>
                                            <ListItemText
                                                primary={<Typography sx={styles.title}>{parseFloat(row?.qty)}</Typography>}
                                                secondary={<Typography sx={styles.subTitle}>{row?.uom_name}</Typography>}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <ListItemText
                                                primary={<Typography sx={styles.title}>-2</Typography>}
                                                secondary={<Typography sx={styles.subTitle}>{row?.uom_name}</Typography>}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <ButtonGroup variant="outlined" size="small" aria-label="outlined button group">
                                                <Button variant="contained">{t('noAction')}</Button>
                                                <Button>{t('adjustment')}</Button>
                                                <Button>{t('issue')}</Button>
                                            </ButtonGroup>
                                        </TableCell>
                                    </TableRow>
                                )
                            })

                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    const RenderFormProfile = () => {

        const {
            document
        } = data

        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={`Profiles`}
                            subTitle={`Related profiles attached to document`}
                        />

                    </Grid>

                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>Location</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {document?.location_name}
                                </StyledContent>
                                <StyledContent2>
                                    {document?.state_name}
                                </StyledContent2>
                            </Grid>

                        </Grid>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDocument = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`document`)}
                            subTitle={t(`basicInformationAboutTheDocument`)}
                        />

                    </Grid>
                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentNo')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_number}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentDate')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_date}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('remark')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_remark}
                                </StyledContent>
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        if (!data) return <></>

        return (
            <>
                {/* <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={5} textAlign={"left"}>
                        {
                            RenderFormDocument()
                        }
                    </Grid>
                    <Grid item xs={12} sm={12} md={7} textAlign={"left"}>
                        {
                            RenderFormProfile()
                        }
                    </Grid>
                </Grid> */}

                <Box
                    sx={{
                        textAlign: 'left',
                        mt: 3,
                        pl: 0.5,
                    }}
                >
                    <CommonSectionHeader
                        title={t(`items`)}
                        subTitle={t(`detailInformationAboutTheItemsInvolved`)}
                    />
                </Box>

                <StyledCard
                    sx={{
                        mt: 1.5,
                        p: 0
                    }}
                >
                    <Grid container rowSpacing={0}>
                        <Grid item md={12}>
                            <RenderTable />
                        </Grid>
                        {/* <Grid item md={12}>
                            <RenderTableFooter />
                        </Grid> */}

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"center"}>
                        <Button
                            type="button"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            onClick={() => navigate(-1)}
                        >
                            {t('back')}
                        </Button>
                    </Grid>
                </Grid>
            </>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.viewStockTake })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={`${t('executeStockTake')} ${data?.document.doc_number}`}
                breadcrumbs={[
                    {
                        displayText: t("stockTakes"),
                        props: {
                            href: getRoute("documents/stockTakes")
                        }
                    },
                    {
                        displayText: `#${data?.document.doc_number}`,
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageExecute.id }) ?
                    (
                        <RenderForm />
                    )
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}